import Spinner from '@alexis/components/Spinner';
import { clsx } from '@alexis/helpers/clsx';
import { gtmTrack } from '@alexis/helpers/gtm';
import { translateNumber } from '@alexis/helpers/translation';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import NormalFare from '@components/flights/Fare/NormalFare';

import { PAYMENT_METHOD_FILTER_BASED_ON_POS } from '@constants/flight';
import { GTM_ID } from '@constants/general';

import { useFlightContext } from '@context/FlightContext';

import { filterFlightFareResultFares } from '@helpers/flight';
import { sortFlightFares } from '@helpers/flightSort';
import { flightsRouteTranslation } from '@helpers/routeTranslation';
import {
  webEngageTrackFareClickOnTripDetailPage,
  webEngageTrackPurchase,
} from '@helpers/webEngage';

import {
  getCurrencyState,
  getCurrentSiteState,
  getLocaleState,
  getTranslationsState,
} from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';
import type { FlightFareResult } from '@wegoTypes/flights/metasearch/flightFareResult';
import type { FlightSearchResultSearch } from '@wegoTypes/flights/metasearch/flightSearchResultSearch';

import styles from './TripFares.module.scss';

export interface TripFaresProps {
  activeLeg: number;
  clientId: string | undefined;
  flightFareResult: FlightFareResult | undefined;
  flightMetasearchInfo: FlightSearchResultSearch | undefined;
  gtmAuth: string;
  gtmPreview: string;
  isLoadingFares: boolean;
  passengerCount: number;
  paymentMethodsFromApi: Array<HomepageFlightsPaymentMethod>;
  sessionId: string | undefined;
  isSpecialFlight: boolean;
  beforeProceed(handoffUrl: string): void;
  impressionLogOnFareClick: (fareId: string) => void;
  impressionLogOnFareFilter: () => void;
}

const TripFares: React.FC<TripFaresProps> = ({
  activeLeg,
  beforeProceed,
  clientId,
  flightFareResult,
  flightMetasearchInfo,
  gtmAuth,
  gtmPreview,
  isLoadingFares,
  passengerCount,
  paymentMethodsFromApi,
  isSpecialFlight,
  sessionId,
  impressionLogOnFareClick,
  impressionLogOnFareFilter,
}) => {
  const currency = useSelector(getCurrencyState);
  const currentSite = useSelector(getCurrentSiteState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const [selectedPaymentMethodIds, setSelectedPaymentMethodIds] = useState<Array<number>>([]);
  const [isNormalFareSectionExpanded, setIsNormalFareSectionExpanded] = useState<boolean>(false);

  const { isApplyBaggageFilter } = useFlightContext();

  const [searchParams] = useSearchParams();
  const providersSearchParam = searchParams.get('providers') ?? '';
  const providerCodesSearchParam = searchParams.get('provider_codes') ?? '';
  const selectedPaymentMethodIdsSearchParam = searchParams.get(
    FlightSearchResultSearchParams.SelectedPaymentMethod,
  );

  const { filteredNormalFares, filteredRecommendedFares } = useMemo(() => {
    if (flightFareResult) {
      return filterFlightFareResultFares(
        flightFareResult.trip,
        selectedPaymentMethodIds,
        !!providerCodesSearchParam ? providerCodesSearchParam.split(':')[activeLeg] : '',
        !!providersSearchParam ? providersSearchParam.split(':')[activeLeg] : '',
        isApplyBaggageFilter,
      );
    }

    return { filteredNormalFares: [], filteredRecommendedFares: [] };
  }, [flightFareResult, providerCodesSearchParam, providersSearchParam, selectedPaymentMethodIds]);

  const selectedPaymentMethodIdsFromParam = useMemo<Array<number>>(() => {
    if (selectedPaymentMethodIdsSearchParam?.split(':')[activeLeg]) {
      const paymentMethodsForActiveLeg = selectedPaymentMethodIdsSearchParam.split(':')[activeLeg];

      return paymentMethodsForActiveLeg.split(',').map((item) => parseInt(item));
    }

    return [];
  }, [activeLeg, selectedPaymentMethodIdsSearchParam]);

  const allFares = useMemo(
    () => [...filteredNormalFares, ...filteredRecommendedFares],
    [filteredNormalFares, filteredRecommendedFares],
  );

  const displayedFilteredNormalFares = useMemo(
    () => (isNormalFareSectionExpanded ? filteredNormalFares : filteredNormalFares.slice(0, 10)),
    [filteredNormalFares, isNormalFareSectionExpanded],
  );

  const availablePaymentMethods = useMemo<Array<FlightSearchResultPaymentMethod>>(() => {
    if (flightFareResult) {
      return flightFareResult.paymentMethods.filter((paymentMethod) =>
        (
          PAYMENT_METHOD_FILTER_BASED_ON_POS[currentSite.countryCode] ||
          PAYMENT_METHOD_FILTER_BASED_ON_POS.default
        ).includes(paymentMethod.name.toLowerCase()),
      );
    }

    return [];
  }, [currentSite.countryCode, flightFareResult]);

  const handlePaymentMethodFilterClick = useCallback<(paymentMethodId: number) => void>(
    (paymentMethodId) => {
      impressionLogOnFareFilter();

      setSelectedPaymentMethodIds((prevState) => {
        if (prevState.includes(paymentMethodId)) {
          return prevState.filter((id) => id !== paymentMethodId);
        }

        return [...prevState, paymentMethodId];
      });
    },
    [],
  );

  const getFareUrl = useCallback(
    (fare: FlightFareResultTripFare) => {
      let handoffUrl = fare.handoffUrl + '&currency_code=' + currency.code;
      if (fare.provider?.instant) {
        const flightRoute = flightsRouteTranslation(locale);
        const urlComponents = [];
        // If there is locale and it's not default
        if (
          locale &&
          currentSite.supportedLocales.includes(locale) &&
          locale !== currentSite.defaultLocale
        ) {
          urlComponents.push(locale);
        }
        urlComponents.push(flightRoute);

        const basePath = urlComponents.join('/');

        handoffUrl = [
          `/${basePath}`,
          'facilitated-bookings',
          fare.id.replace(/\./g, '_'),
          `?currency_code=${currency.code}`,
        ].join('/');
      } else if (fare.provider?.wegoFare) {
        let wegoCampaignParameters = '';
        let bowOnly = '';
        // Append Wego Campaign parameter to BoW handoff url
        for (const param of searchParams) {
          if (param[0].startsWith('wg_') || param[0] === 'ts_code') {
            wegoCampaignParameters += `&${param.join('=')}`;
          }
          // Append bow_only parameter to BoW handoff url
          if (param[0] === 'bow_only') {
            bowOnly += `&${param.join('=')}`;
          }
        }

        // Replace the origin of the handoff url with the current origin, for branch based staging
        const currentOrigin = window.location.origin;
        const updatedUrl = fare.handoffUrl.replace(/^https?:\/\/[^/]+/, currentOrigin);
        return `${updatedUrl}${wegoCampaignParameters}${bowOnly}`;
      }

      if (clientId) {
        handoffUrl += '&wc=' + clientId;
      }
      if (sessionId) {
        handoffUrl += '&ws=' + sessionId;
      }

      const existingQueryParams = new URLSearchParams(window.location.search);
      existingQueryParams.delete('search_id'); //Search id is already present in handoff url

      handoffUrl += `&${existingQueryParams.toString()}`;

      return handoffUrl;
    },
    [
      clientId,
      currency.code,
      currentSite.defaultLocale,
      currentSite.supportedLocales,
      locale,
      searchParams,
      sessionId,
    ],
  );

  const handleViewDealClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>,
      fare: FlightFareResultTripFare,
      isSpecialFlightFare: boolean,
    ): void => {
      e.stopPropagation();

      impressionLogOnFareClick(fare.id);

      const cheapestFare = sortFlightFares(filteredNormalFares, 'asc', true)[0];
      const isSelectedFareCheapest = cheapestFare?.id === fare.id;

      const gtmData = {
        event: 'eventTracked',
        eventAction: fare.provider?.instant ? 'fb' : 'handoff',
        eventCategory: 'flights',
        eventLabel: isSelectedFareCheapest ? 'su-low-fare' : 'non-su',
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);

      if (!!flightFareResult && !!flightMetasearchInfo) {
        const { legs, adultsCount, childrenCount, infantsCount, cabin } = flightMetasearchInfo;

        webEngageTrackFareClickOnTripDetailPage(
          currentSite.countryCode,
          locale,
          adultsCount,
          childrenCount,
          infantsCount,
          cabin,
          legs,
          flightFareResult.trip.legs,
          flightFareResult.trip.id,
          allFares,
          fare,
        );

        // TODO: Following code may we can use if Sprinklr Supports
        /* if (!isMulticity(legs)) {
          const firstLeg = legs[0];
          const secondLeg = legs[1];

          const data = [
            `provider_code: ${fare.provider.code}`,
            `origin: ${firstLeg?.departureAirport?.name || firstLeg.departureCity.name}`,
            `destination: ${firstLeg?.arrivalAirport?.name || firstLeg.arrivalCity.name}`,
            `dep_date: ${firstLeg?.outboundDate}`,
            `arr_date: ${secondLeg?.outboundDate}`,
            `session_date: ${new Date()}`,
          ];

          // Expires in 2 weeks
          addCookie(
            'wego_fchat_fhandoff',
            data.join(' | '),
            new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).getTime(),
          );
        }*/
      }

      if (!fare.provider?.wegoFare) {
        webEngageTrackPurchase();
      } else {
        if (isSpecialFlightFare) {
          e.preventDefault();
          beforeProceed(getFareUrl(fare));
        }
        return;
      }

      if (isSpecialFlightFare) {
        beforeProceed(getFareUrl(fare));
      } else {
        window.open(getFareUrl(fare), '_blank');
      }

      return;
    },
    [
      allFares,
      currentSite.countryCode,
      flightMetasearchInfo,
      getFareUrl,
      locale,
      flightFareResult,
      filteredNormalFares,
    ],
  );

  // Preselecting the payment filters that is selected in Filter sidebar
  useEffect(() => {
    if (selectedPaymentMethodIdsFromParam.length > 0) {
      setSelectedPaymentMethodIds(selectedPaymentMethodIdsFromParam);
    }
  }, [selectedPaymentMethodIdsFromParam]);

  return (
    <div className={styles.container}>
      {/* Section: Flexibilities  Filter */}
      {availablePaymentMethods.length > 0 && (
        <div className={styles.filters}>
          {translations.filter_by}

          {availablePaymentMethods.map((paymentMethod) => (
            <div
              key={paymentMethod.id}
              className={clsx(
                styles.filterButton,
                selectedPaymentMethodIds.includes(paymentMethod.id) && styles.active,
              )}
              onClick={() => handlePaymentMethodFilterClick(paymentMethod.id)}
            >
              {paymentMethod.name}
            </div>
          ))}
        </div>
      )}

      {flightFareResult ? (
        <>
          {/* Fare Options Title */}
          <div className={styles.fareOptionsTitle}>
            <div className={styles.fareOptionsLabel}>
              {translations.fare_options}{' '}
              {filteredNormalFares.length
                ? `(${translateNumber(filteredNormalFares.length, locale === 'fa')})`
                : ''}
            </div>

            <div className={styles.fareOptionsNoteText}>{translations.flight_price}</div>
          </div>

          {/* Normal Fares */}
          {filteredNormalFares.length > 0 && (
            <div className={styles.fares}>
              {displayedFilteredNormalFares.map((fare, index) => {
                return (
                  <NormalFare
                    key={fare.id}
                    fare={fare}
                    fareIndex={index}
                    fareUrl={getFareUrl(fare)}
                    filteredFares={filteredNormalFares}
                    flightFareResult={flightFareResult}
                    isLoadingFares={isLoadingFares}
                    isSpecialFlight={isSpecialFlight}
                    passengerCount={passengerCount}
                    paymentMethodsFromApi={paymentMethodsFromApi}
                    onViewDealClick={handleViewDealClick}
                  />
                );
              })}

              {filteredNormalFares.length > 10 && (
                <div
                  className={styles.showLessMoreButtonContainer}
                  onClick={() => setIsNormalFareSectionExpanded((prev) => !prev)}
                >
                  <div
                    className={clsx(
                      styles.showLessMoreButton,
                      isNormalFareSectionExpanded && styles.expanded,
                    )}
                  >
                    {isNormalFareSectionExpanded ? translations.show_less : translations.show_more}
                    <div className={clsx(styles.chevron, styles.bottom)}></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className={styles.loading}>
          {translations.load_more_options}...
          <Spinner className={styles.spinner} />
        </div>
      )}
    </div>
  );
};

export default TripFares;
